import { action, ActionType } from 'typesafe-actions';
import { Dispatch } from 'redux';

import { getBFFData, Queries } from '../api';
import types from './actionTypes';
import { Product, Size, YotpoBottomline, YotpoReview } from './types';
import { cmsProductInit } from '../cms/actions';
import { YOTPO_API_KEY } from '../api/constants';

export const requestProduct = () => action(types.REQUEST_PRODUCT_LOADING, null);
export const requestProductSuccess = (payload: Product) =>
  action(types.REQUEST_PRODUCT_SUCCESS, payload);

export const requestProductSuccessModal = (payload: Product) =>
  action(types.REQUEST_PRODUCT_MODAL, payload);

export const changeSizeModal = (payload: Size) => action(types.CHANGE_SIZE_MODAL, payload);

export const requestProductFailure = (payload: string) =>
  action(types.REQUEST_PRODUCT_FAILURE, payload);

export const changeSize = (payload: Size) => action(types.CHANGE_SIZE, payload);
export const setSizeErrMsg = (payload: string) => action(types.SET_SIZE_ERR_MSG, payload);
export const requestYotpoSuccess = (payload: {
  bottomline: YotpoBottomline;
  reviews: YotpoReview[];
}) => action(types.REQUEST_YOTPO_SUCCESS, payload);

type AsyncProductActions = ActionType<
  | typeof requestProduct
  | typeof requestProductSuccess
  | typeof requestProductFailure
  | typeof requestProductSuccessModal
  | typeof requestYotpoSuccess
>;

export type ProductActions =
  | AsyncProductActions
  | ActionType<
      | typeof changeSize
      | typeof setSizeErrMsg
      | typeof changeSizeModal
      | typeof setCrossSellLoading
      | typeof setUpSellLoading
    >;

export type LoadProductParams = {
  productRef: string;
  colorRef: string;
};

export function loadProduct({ productRef, colorRef }: LoadProductParams) {
  return async (dispatch: Dispatch<ProductActions>) => {
    dispatch(requestProduct());
    const response = await getBFFData(Queries.getProduct, { productRef, colorRef });
    if (response.ok) {
      const product = response.data.product ?? {};
      dispatch(requestProductSuccess(product));
    } else {
      dispatch(requestProductFailure(response.data));
    }
  };
}

async function fetchYotpoSSRData(productRef: string, dispatch: Dispatch<ProductActions>) {
  if (!YOTPO_API_KEY) {
    return;
  }
  try {
    const headers = { 'Content-Type': 'application/json', accept: 'application/json' };

    const [bottomlineRes, reviewsRes] = await Promise.all([
      fetch(`https://api-cdn.yotpo.com/products/${YOTPO_API_KEY}/${productRef}/bottomline`, {
        headers,
      }),
      fetch(
        `https://api-cdn.yotpo.com/v1/widget/${YOTPO_API_KEY}/products/${productRef}/reviews.json?per_page=10&sort=date&direction=desc`,
        { headers }
      ),
    ]);

    if (!bottomlineRes.ok || !reviewsRes.ok) {
      return;
    }

    const [bottomlineData, reviewsData] = await Promise.all([
      bottomlineRes.json(),
      reviewsRes.json(),
    ]);

    const bottomline: YotpoBottomline = bottomlineData?.response?.bottomline;
    const reviews: YotpoReview[] = reviewsData?.response?.reviews ?? [];

    if (bottomline) {
      dispatch(requestYotpoSuccess({ bottomline, reviews }));
    }
  } catch {
    // non-critical — structured data degrades gracefully without Yotpo data
  }
}

export function productInit({ productRef, colorRef }: LoadProductParams) {
  return async (dispatch) => {
    await Promise.all([
      dispatch(loadProduct({ productRef, colorRef })),
      dispatch(cmsProductInit()),
      fetchYotpoSSRData(productRef, dispatch),
    ]);
  };
}

export const setCrossSellLoading = (payload: boolean) =>
  action(types.SET_CROSS_SELL_LOADING, payload);

export const setUpSellLoading = (payload: boolean) => action(types.SET_UP_SELL_LOADING, payload);
